import { AnalyticsBrowser } from '@segment/analytics-next';
import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { BusinessSegment, BusinessType } from '../api-client';
import type { FormContextData } from '../state';
import { getCountryCodeFromTLD } from '../helpers';

// For segment tracking, we set the country code based on the current domain name
const country = getCountryCodeFromTLD();

export enum SegmentEventType {
  BUSINESS_SEARCH = 'business_search',
  ADD_INFO = 'add_info',
  ADD_SERVICES = 'add_services',
  ADD_BUSINESS_TYPE = 'add_business_type',
  ABOUT_SIGN_UP = 'self_sign_up_click',
  ERROR_MESSAGE_SHOWN = 'error_message_shown',
  LOGIN_REDIRECT = 'login_redirect',
  CONTINUE_WITH_SIGNUP = 'continue_with_signup',
  CONTEXT_STEP_CLICK = 'context_step_click',
}

type BusinessSearchPayload = {
  step: 'AddInfo';
  action: 'search' | 'manual' | 'skip';
};

type AddInfoPayload = {
  step: 'AddInfo';
  mode: 'manual' | 'automatic';
};
type AddServicesPayload = {
  step: 'AddServices';
  services_selection: BusinessSegment[];
};
type AddBusinessTypePayload = {
  step: 'AddBusinessType';
  business_selection: BusinessType;
};
type AboutSignUpPayload = {
  step: 'AboutSignUp';
  marketing_opt_in: boolean;
};
type LoginRedirectPayload = {
  step: 'LoginRedirect';
};
type ContinueWithSignupPayload = {
  step: 'LoginRedirect';
};
type ContextStepPayload = {
  step: 'ContextStep';
};
export type ErrorMessageShownPayload = {
  step:
    | 'AboutSignUp'
    | 'AddInfo'
    | 'AddServices'
    | 'AddBusinessType'
    | 'VerificationCode'
    | 'unknown';
  errors: string[];
};

type BusinessSearchFunction = (
  event: SegmentEventType.BUSINESS_SEARCH,
  payload: BusinessSearchPayload,
) => void;
type AddInfoFunction = (
  event: SegmentEventType.ADD_INFO,
  payload: AddInfoPayload,
) => void;
type AddServicesFunction = (
  event: SegmentEventType.ADD_SERVICES,
  payload: AddServicesPayload,
) => void;
type AddBusinessTypeFunction = (
  event: SegmentEventType.ADD_BUSINESS_TYPE,
  payload: AddBusinessTypePayload,
) => void;
type AboutSignUpFunction = (
  event: SegmentEventType.ABOUT_SIGN_UP,
  payload: AboutSignUpPayload,
) => void;
type ErrorMessageShownFunction = (
  event: SegmentEventType.ERROR_MESSAGE_SHOWN,
  payload: ErrorMessageShownPayload,
) => void;
type LoginRedirectFunction = (
  event: SegmentEventType.LOGIN_REDIRECT,
  payload: LoginRedirectPayload,
) => void;
type ContinueWithSignupFunction = (
  event: SegmentEventType.CONTINUE_WITH_SIGNUP,
  payload: ContinueWithSignupPayload,
) => void;
type ContextStepFunction = (
  event: SegmentEventType.CONTEXT_STEP_CLICK,
  payload: ContextStepPayload,
) => void;

type SegmentContext = {
  trackEvent: BusinessSearchFunction &
    AddInfoFunction &
    AddServicesFunction &
    AddBusinessTypeFunction &
    AboutSignUpFunction &
    ErrorMessageShownFunction &
    LoginRedirectFunction &
    ContinueWithSignupFunction &
    ContextStepFunction;
  identify: (
    params: Pick<
      FormContextData,
      'firstName' | 'lastName' | 'phoneNumber' | 'email'
    >,
  ) => void;
};

const SegmentContext = createContext<SegmentContext>({
  trackEvent: () => {},
  identify: () => {},
});

const segment = process.env.SEGMENT_WRITE_KEY
  ? AnalyticsBrowser.load({ writeKey: process.env.SEGMENT_WRITE_KEY })
  : null;

export function SegmentContextProvider(props: PropsWithChildren) {
  const trackEvent = useCallback<SegmentContext['trackEvent']>(
    (event: SegmentEventType, payload: Record<string, unknown>) => {
      if (!segment) {
        return;
      }

      segment.track(event, { ...payload, country });
    },
    [],
  );

  const identify = useCallback<SegmentContext['identify']>(
    (
      params: Pick<
        FormContextData,
        'firstName' | 'lastName' | 'phoneNumber' | 'email'
      >,
    ) => {
      if (!segment) {
        return;
      }

      const { firstName, lastName, phoneNumber: phone, email } = params;
      segment.identify({ firstName, lastName, phone, email });
    },
    [],
  );

  useEffect(() => {
    if (!segment) {
      return;
    }

    segment.page(undefined, undefined, { country });
  }, []);

  const value = useMemo(
    () => ({ trackEvent, identify }),
    [trackEvent, identify],
  );

  return (
    <SegmentContext.Provider value={value}>
      {props.children}
    </SegmentContext.Provider>
  );
}

export function useSegmentContext() {
  return useContext(SegmentContext);
}
